/**
 * Unified email/phone field that switches mode from the typed value (create + edit).
 */

import * as React from 'react';
import { useMutation } from '@tanstack/react-query';
import { Input } from '@/components/forms/Input';
import { PhoneInput } from '@/components/forms/PhoneInput';
import { Button } from '@/components/forms/Button';
import { Mail, Send, KeyRound } from 'lucide-react';
import { detectIdentifierType } from '@/utils/auth-validation-utils';
import { COUNTRY_CODE_MAP } from '@/constants/phone-countries-catalog';
import {
  isPrimaryContactDirty,
  parseStoredPhone,
  resolveStoredContact,
  useDefaultPhoneCountry,
} from '@/utils/user-contact-utils';
import type { PrimaryContactKind } from '@/types/user.types';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';
import { regenerateInvitation, sendPasswordReset } from '@/services/users.service';

/** Widget mode: undecided until the typed value looks like email or phone. */
type ContactMode = 'initial' | 'email' | 'phone';
/** Detected / emitted contact kind (`unknown` until a type is inferred). */
type ContactType = 'email' | 'phone' | 'unknown';

/** Stay in email mode, or fall back when `@` disappears. */
function handleEmailInputChange(
  e: React.ChangeEvent<HTMLInputElement>,
  defaultCountry: string,
  detectedCountryRef: React.RefObject<string>,
  setMode: (m: ContactMode) => void,
  setDetectedCountry: (c: string) => void,
  onContactChange: (value: string, type: ContactType, phoneCountryCode?: string) => void,
) {
  const inputValue = e.target.value;
  if (!inputValue.includes('@')) {
    const detectedType = detectIdentifierType(inputValue);
    if (detectedType === 'phone') {
      setMode('phone');
      detectedCountryRef.current = defaultCountry;
      setDetectedCountry(defaultCountry);
      onContactChange(inputValue, 'phone', defaultCountry);
    } else {
      setMode('initial');
      onContactChange(inputValue, 'unknown');
    }
    return;
  }
  onContactChange(inputValue, 'email');
}

/** Internal email/phone mode, refs, and change handlers. */
interface ContactInputState {
  mode: ContactMode;
  detectedCountry: string;
  internalDisplayValue: string;
  phoneInputRef: React.RefObject<HTMLInputElement>;
  emailInputRef: React.RefObject<HTMLInputElement>;
  handleInitialInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleEmailChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handlePhoneChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handlePhoneCountryChange: (country: { code: string }) => void;
}

/** All stateful contact-input logic extracted from the component body. */
function useContactInputState(
  disabled: boolean,
  storedContact: StoredContact | undefined,
  value: string,
  onContactChange: (value: string, type: ContactType, phoneCountryCode?: string) => void,
): ContactInputState {
  const initialStoredEmail = storedContact?.email;
  const initialStoredPhone = storedContact?.phone;
  const primaryContactKind = storedContact?.primaryKind;
  const defaultCountry = useDefaultPhoneCountry();

  const _resolvedOnMount =
    disabled && (initialStoredEmail || initialStoredPhone)
      ? resolveStoredContact(
          initialStoredEmail ?? null,
          initialStoredPhone ?? null,
          primaryContactKind,
        )
      : null;

  // For non-disabled edit fields, seed mode from primaryContactKind so action buttons are visible on mount.
  function resolveInitialMode(): ContactMode {
    if (_resolvedOnMount) return _resolvedOnMount.mode as ContactMode;
    if (primaryContactKind === 'phone') return 'phone';
    if (primaryContactKind === 'email') return 'email';
    return 'initial';
  }
  const _initialMode: ContactMode = resolveInitialMode();

  const [mode, setMode] = React.useState<ContactMode>(_initialMode);
  const [detectedCountry, setDetectedCountry] = React.useState<string>(
    _resolvedOnMount ? _resolvedOnMount.isoCode : defaultCountry,
  );
  const detectedCountryRef = React.useRef<string>(
    _resolvedOnMount ? _resolvedOnMount.isoCode : defaultCountry,
  );
  const [internalDisplayValue, setInternalDisplayValue] = React.useState<string>(
    _resolvedOnMount ? _resolvedOnMount.displayValue : '',
  );
  const phoneInputRef = React.useRef<HTMLInputElement>(null);
  const emailInputRef = React.useRef<HTMLInputElement>(null);
  const prevMode = React.useRef<ContactMode>(_initialMode);
  const sortedDialCodes = React.useMemo(
    () => Object.keys(COUNTRY_CODE_MAP).sort((a, b) => b.length - a.length),
    [],
  );

  React.useEffect(() => {
    if (mode === prevMode.current) return;
    prevMode.current = mode;
    if (mode === 'phone') phoneInputRef.current?.focus();
    else if (mode === 'email') emailInputRef.current?.focus();
  }, [mode]);

  React.useEffect(() => {
    if (!disabled || (!initialStoredEmail && !initialStoredPhone)) return;
    const resolved = resolveStoredContact(
      initialStoredEmail,
      initialStoredPhone,
      primaryContactKind,
    );
    prevMode.current = resolved.mode as ContactMode;
    setMode(resolved.mode as ContactMode);
    detectedCountryRef.current = resolved.isoCode;
    setDetectedCountry(resolved.isoCode);
    setInternalDisplayValue(resolved.displayValue);
    onContactChange(resolved.displayValue, resolved.mode, resolved.isoCode);
  }, [disabled, initialStoredEmail, initialStoredPhone, primaryContactKind]);

  // Sync mode and country when primaryKind changes on a non-disabled edit field (e.g. after a contact swap).
  const prevPrimaryContactKind = React.useRef<PrimaryContactKind | null | undefined>(
    primaryContactKind,
  );
  React.useEffect(() => {
    if (disabled) return;
    if (!primaryContactKind) return;
    if (primaryContactKind === prevPrimaryContactKind.current) return;
    prevPrimaryContactKind.current = primaryContactKind;
    const targetMode: ContactMode = primaryContactKind === 'phone' ? 'phone' : 'email';
    prevMode.current = targetMode;
    setMode(targetMode);
    if (primaryContactKind === 'phone' && value?.startsWith('+')) {
      const parsed = parseStoredPhone(value, defaultCountry);
      detectedCountryRef.current = parsed.isoCode;
      setDetectedCountry(parsed.isoCode);
    }
  }, [disabled, primaryContactKind, value, defaultCountry]);

  React.useEffect(() => {
    if (!disabled || initialStoredEmail || initialStoredPhone || !value.trim()) return;
    const detected = detectIdentifierType(value);
    if (detected !== 'email' && detected !== 'phone') return;
    prevMode.current = detected;
    setMode(detected);
    setInternalDisplayValue(value);
  }, [disabled, value, initialStoredEmail, initialStoredPhone]);

  /** Detect email vs phone (or a pasted dial code) from the first keystrokes. */
  const handleInitialInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const dialSwitch = resolveDialCodeSwitch(inputValue, sortedDialCodes, defaultCountry);
    if (dialSwitch) {
      detectedCountryRef.current = dialSwitch.country;
      setDetectedCountry(dialSwitch.country);
      setMode('phone');
      onContactChange(dialSwitch.value, 'phone', dialSwitch.country);
      return;
    }
    const detectedType = detectIdentifierType(inputValue);
    if (detectedType === 'email') {
      setMode('email');
      onContactChange(inputValue, 'email');
    } else if (detectedType === 'phone') {
      setMode('phone');
      detectedCountryRef.current = defaultCountry;
      setDetectedCountry(defaultCountry);
      onContactChange(inputValue, 'phone', defaultCountry);
    } else {
      setMode('initial');
      onContactChange(inputValue, 'unknown');
    }
  };

  /** Persist email digits and switch mode if the value becomes a phone. */
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    handleEmailInputChange(
      e,
      defaultCountry,
      detectedCountryRef,
      setMode,
      setDetectedCountry,
      onContactChange,
    );

  /** Persist local phone digits with the current country. */
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onContactChange(e.target.value, 'phone', detectedCountryRef.current);
  };

  /** Persist the picked country and re-emit the current phone. */
  const handlePhoneCountryChange = (country: { code: string }) => {
    detectedCountryRef.current = country.code;
    setDetectedCountry(country.code);
    onContactChange(value, 'phone', country.code);
  };

  return {
    mode,
    detectedCountry,
    internalDisplayValue,
    phoneInputRef,
    emailInputRef,
    handleInitialInputChange,
    handleEmailChange,
    handlePhoneChange,
    handlePhoneCountryChange,
  };
}

/**
 * Returns null when no dial code is matched yet (caller should fall through to {@link detectIdentifierType}).
 */
function resolveDialCodeSwitch(
  inputValue: string,
  sortedDialCodes: string[],
  defaultCountry: string,
): { mode: ContactMode; value: string; country: string } | null {
  if (!inputValue.startsWith('+')) return null;
  const afterPlus = inputValue.slice(1);
  for (const dialCode of sortedDialCodes) {
    if (afterPlus.startsWith(dialCode)) {
      return {
        mode: 'phone',
        value: afterPlus.slice(dialCode.length).replace(/^\s+/, ''),
        country: COUNTRY_CODE_MAP[dialCode],
      };
    }
  }
  return { mode: 'phone', value: inputValue, country: defaultCountry };
}

/** Mutations for resend-invitation and reset-password action buttons. */
function useContactActionMutations(
  userId: string | undefined,
  ownProfile: boolean,
  storedContact: StoredContact | undefined,
  currentValue: string,
  currentMode: ContactMode,
) {
  const resendInvitation = useMutation({
    mutationFn: () => regenerateInvitation(userId!),
    onSuccess: () => {
      toast.success("Un nouveau lien d'activation a été envoyé par email.");
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error));
    },
  });

  const resetPassword = useMutation({
    mutationFn: () => sendPasswordReset(userId!),
    onSuccess: () => {
      toast.success(
        ownProfile
          ? 'Un email de réinitialisation de votre mot de passe a été envoyé.'
          : 'Un email de réinitialisation de mot de passe a été envoyé.',
      );
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error));
    },
  });

  const defaultCountry = useDefaultPhoneCountry();
  const isContactDirty = React.useMemo(() => {
    if (!storedContact) return false;
    return isPrimaryContactDirty({
      mode: currentMode,
      currentValue,
      storedEmail: storedContact.email,
      storedPhone: storedContact.phone,
      serverKind: storedContact.serverPrimaryKind,
      defaultCountry,
    });
  }, [storedContact, currentValue, currentMode, defaultCountry]);

  return { resendInvitation, resetPassword, isContactDirty };
}

/** Server-side hydration bundle: DB values used to seed the field on mount and after a swap. */
export interface StoredContact {
  /** Which contact is the primary identifier — drives the initial mode and syncs after a swap. */
  primaryKind: PrimaryContactKind | null | undefined;
  /** Server primary kind — dirty-check only; do not overwrite with the live form type. */
  serverPrimaryKind?: PrimaryContactKind | null;
  /** Stored email (raw DB value). Only needed for disabled/read-only fields. */
  email?: string | null;
  /** Stored phone (E.164 or local, raw DB value). Only needed for disabled/read-only fields. */
  phone?: string | null;
}

/** Props for {@link SmartContactInput}. */
interface SmartContactInputProps {
  value: string;
  onContactChange: (value: string, type: ContactType, phoneCountryCode?: string) => void;
  onBlur?: () => void;
  disabled?: boolean;
  required?: boolean;
  label?: string;
  labelTooltip?: string;
  error?: string;
  state?: 'default' | 'error' | 'success' | 'warning';
  helperText?: string;
  inputSize?: 'sm' | 'md' | 'lg';
  /** Edit-mode hydration: DB values to seed the field on mount and sync after a swap. */
  storedContact?: StoredContact;
  /** If true, show action buttons (edit forms only). Never show on create forms. */
  showContactActions?: boolean;
  /** Required when showContactActions=true: controls which button to show. */
  emailVerified?: boolean;
  /** Required when showContactActions=true: userId for API call. */
  userId?: string;
  /** True when student remote uses inherited parent credentials — hides all action buttons. */
  usesParentCredentials?: boolean;
  /** True when the form belongs to the signed-in user themselves (MyProfile). Adapts button labels and toasts to first-person. */
  ownProfile?: boolean;
  /** Swap control, centered on the input row (not the label). */
  swapButton?: React.ReactNode;
}

/** Email/phone field that switches mode from the typed value. */
export const SmartContactInput: React.FC<SmartContactInputProps> = ({
  value,
  onContactChange,
  onBlur,
  disabled = false,
  required = false,
  label = 'Contact',
  labelTooltip,
  error,
  state,
  helperText,
  inputSize = 'md',
  storedContact,
  showContactActions = false,
  emailVerified,
  userId,
  usesParentCredentials = false,
  ownProfile = false,
  swapButton,
}) => {
  const {
    mode,
    detectedCountry,
    internalDisplayValue,
    phoneInputRef,
    emailInputRef,
    handleInitialInputChange,
    handleEmailChange,
    handlePhoneChange,
    handlePhoneCountryChange,
  } = useContactInputState(disabled, storedContact, value, onContactChange);

  const displayValue = disabled ? internalDisplayValue || value : value;
  let fieldContent: React.ReactNode;

  const {
    resendInvitation: resendInvitationMutation,
    resetPassword: resetPasswordMutation,
    isContactDirty,
  } = useContactActionMutations(userId, ownProfile, storedContact, value, mode);

  const canShowContactActions = showContactActions && !usesParentCredentials;
  const showResendButton =
    canShowContactActions && !isContactDirty && mode === 'email' && emailVerified === false;
  const showResetButton =
    canShowContactActions && !isContactDirty && mode === 'email' && emailVerified === true;

  function handleResendInvitation(): void {
    resendInvitationMutation.mutate();
  }

  function handleResetPassword(): void {
    resetPasswordMutation.mutate();
  }

  function renderResendButton(): React.ReactNode {
    return (
      <div className="w-full mt-2">
        <Button
          type="button"
          variant="secondary"
          size="sm"
          icon={Send}
          loading={resendInvitationMutation.isPending}
          disabled={resendInvitationMutation.isPending}
          onClick={handleResendInvitation}
          fullWidth
        >
          Renvoyer l'invitation
        </Button>
      </div>
    );
  }

  function renderResetButton(): React.ReactNode {
    return (
      <div className="w-full mt-2">
        <Button
          type="button"
          variant="secondary"
          size="sm"
          icon={KeyRound}
          loading={resetPasswordMutation.isPending}
          disabled={resetPasswordMutation.isPending}
          onClick={handleResetPassword}
          fullWidth
        >
          {ownProfile ? 'Réinitialiser mon mot de passe' : 'Réinitialiser le mot de passe'}
        </Button>
      </div>
    );
  }

  function renderContactActionButtons(): React.ReactNode {
    if (showResendButton) return renderResendButton();
    if (showResetButton) return renderResetButton();
    return null;
  }

  if (mode === 'phone') {
    fieldContent = (
      <PhoneInput
        ref={phoneInputRef}
        label={label}
        labelTooltip={labelTooltip}
        value={displayValue}
        onChange={handlePhoneChange}
        onBlur={onBlur}
        onCountryChange={handlePhoneCountryChange}
        countryCode={detectedCountry}
        disabled={disabled}
        required={required}
        error={error}
        state={state}
        helperText={helperText}
        inputSize={inputSize}
        controlEnd={swapButton}
      />
    );
  } else if (mode === 'email') {
    fieldContent = (
      <Input
        ref={emailInputRef}
        type="text"
        label={label}
        labelTooltip={labelTooltip}
        value={displayValue}
        onChange={handleEmailChange}
        onBlur={onBlur}
        disabled={disabled}
        required={required}
        placeholder="Ex: marie.martin@example.com"
        icon={Mail}
        error={error}
        state={state}
        helperText={helperText}
        inputSize={inputSize}
        controlEnd={swapButton}
      />
    );
  } else {
    fieldContent = (
      <Input
        type="text"
        label={label}
        labelTooltip={labelTooltip}
        value={displayValue}
        onChange={handleInitialInputChange}
        onBlur={onBlur}
        disabled={disabled}
        required={required}
        placeholder="Email ou téléphone"
        error={error}
        state={state}
        helperText={
          helperText ??
          "Saisissez un email ou un numéro de téléphone — le champ s'adaptera automatiquement."
        }
        inputSize={inputSize}
        controlEnd={swapButton}
      />
    );
  }

  if (disabled && labelTooltip) {
    return (
      <div className="w-full">
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="w-full cursor-not-allowed">{fieldContent}</div>
            </TooltipTrigger>
            <TooltipContent>
              <p>{labelTooltip}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        {renderContactActionButtons()}
      </div>
    );
  }

  return (
    <div className="w-full">
      {fieldContent}
      {renderContactActionButtons()}
    </div>
  );
};
